<script lang="ts">
  import { createDropdownMenu } from "@melt-ui/svelte";
  import { get } from "svelte/store";
  import { setContext, type Snippet } from "svelte";
  import type { ClassValue } from "svelte/elements";

  interface Props {
    forceVisible?: boolean;
    loop?: boolean;
    direction?: "bottom-start" | "bottom-end" | "top-start" | "top-end" | undefined;
    portal?: string | HTMLElement | null | undefined;
    sameWidth?: boolean;
    typeahead?: boolean;
    disableFocusFirstItem?: boolean;
    gutter?: number;
    children?: Snippet;
    class?: ClassValue;
  }

  let {
    forceVisible = false,
    loop = false,
    direction = "bottom-start",
    portal,
    sameWidth = false,
    typeahead = true,
    disableFocusFirstItem = false,
    gutter,
    children,
    ...rest
  }: Props = $props();

  // svelte-ignore state_referenced_locally
  const menu = createDropdownMenu({
    forceVisible,
    loop,
    portal,
    typeahead,
    disableFocusFirstItem,
    positioning: {
      placement: direction,
      sameWidth,
      ...(gutter !== undefined ? { gutter } : {}),
    },
  });

  setContext("menu", {
    ...menu,
    submenu: menu.builders.createSubmenu(),
  });

  // keys that are part of text editing when combined with cmd/ctrl
  const TEXT_EDITING_KEYS = new Set([
    "a",
    "c",
    "v",
    "x",
    "z",
    "backspace",
    "delete",
    "arrowleft",
    "arrowright",
  ]);

  // close on keyboard shortcuts (modifier + key) that open other UI,
  // but skip text-editing shortcuts when focus is inside an input
  $effect(() => {
    function handleKeydown(e: KeyboardEvent) {
      if (!get(menu.states.open)) return;
      if (!(e.metaKey || e.ctrlKey) || ["Meta", "Control"].includes(e.key)) return;

      const active = document.activeElement;
      const isTextInput =
        active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
      if (isTextInput && TEXT_EDITING_KEYS.has(e.key.toLowerCase())) return;

      menu.states.open.set(false);
    }
    document.addEventListener("keydown", handleKeydown, true);
    return () => document.removeEventListener("keydown", handleKeydown, true);
  });
</script>

<div {...rest}>
  {@render children?.()}
</div>
